import { Link } from "@/lib/router-compat";
import { SupportingVisual } from "./Visuals";
import {
  SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_COORDINATED,
  SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_COORDINATED_ALT,
  SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_COORDINATED_CAPTION,
} from "@/data/blogData";

const linkCls = "text-[#00A3D9] underline font-medium hover:text-[#0B1F3D]";
const extLinkCls = linkCls;

/** Intro paragraphs + lead excerpt + Table of Contents + Sections 1–3. */
export function BodyPart1({ toc }: { toc: React.ReactNode }) {
  return (
    <>
      {/* Lead Excerpt Summary Box */}
      <div className="bg-[#0B1F3D]/5 border-l-4 border-[#00A3D9] p-6 rounded-r-xl mb-10">
        <p className="text-base md:text-lg text-[#0B1F3D] leading-relaxed font-medium">
          A practical guide to planning a South Florida pool remodel, including
          resurfacing, waterline tile, coping, pool decks, project scope, costs,
          sequencing, and material coordination.
        </p>
      </div>

      {/* Intro paragraphs */}
      <div className="prose prose-lg max-w-none text-foreground leading-relaxed space-y-4 mb-10">
        <p>
          A pool remodel can be as focused as renewing the interior finish or as
          broad as updating the finish, waterline tile, coping, pool deck,
          pavers, and surrounding outdoor space together.
        </p>
        <p>
          The right scope depends on three things: the pool&rsquo;s current
          condition, which components are connected to the planned work, and
          what you want the finished space to accomplish.
        </p>
        <p>
          <strong>
            For most South Florida homeowners, the smartest first step is not
            choosing a tile color or deck material. It is defining what needs to
            change, what can remain, and which parts of the project should be
            coordinated before construction begins.
          </strong>
        </p>
        <p>
          This guide walks through those decisions without assuming every aging
          pool needs a complete overhaul.
        </p>
      </div>

      {toc}

      {/* Section 1 */}
      <section
        id="what-can-a-pool-remodel-include"
        className="scroll-mt-28 py-6 border-t border-border"
      >
        <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3D] mb-4">
          What Can a Pool Remodel Include?
        </h2>
        <div className="prose prose-lg max-w-none text-foreground leading-relaxed space-y-4">
          <p>
            &ldquo;Pool remodeling&rdquo; is a broad term. Two contractors can
            use it to describe projects with very different scopes.
          </p>
          <p>
            A focused project may involve only the pool&rsquo;s interior finish.
            A coordinated remodel may also address waterline tile, coping,
            decking, pavers, features, or transitions into the surrounding
            patio.
          </p>
          <p>
            Depending on the pool and the homeowner&rsquo;s goals, a remodel may
            include:
          </p>
          <ul>
            <li>Renewing or replacing the interior finish</li>
            <li>Replacing waterline tile</li>
            <li>Updating or replacing pool coping</li>
            <li>Remodeling the pool deck</li>
            <li>Installing pavers or coordinated hardscape materials</li>
            <li>Updating verified water or fire features</li>
            <li>
              Changing portions of the pool area to improve its appearance or
              usability
            </li>
            <li>Coordinating the pool with a larger outdoor-living project</li>
          </ul>
          <p>
            Not every remodel should include every item on that list. Sound
            components do not need to be replaced simply because another part of
            the pool is being updated.
          </p>
          <p>
            The purpose of early planning is to separate necessary work from
            optional improvements&mdash;and then determine which items make
            sense to complete together.
          </p>
          <p>
            The{" "}
            <a
              href="https://www.phta.org/consumer/maintenance/remodeling-repairs/"
              target="_blank"
              rel="noopener noreferrer"
              className={extLinkCls}
            >
              Pool &amp; Hot Tub Alliance
            </a>{" "}
            also distinguishes among repairs, resurfacing, and full renovation.
            The written project scope matters more than the label placed on it.
          </p>
        </div>
      </section>

      {/* Section 2 */}
      <section
        id="focused-resurfacing-vs-broader-pool-remodeling"
        className="scroll-mt-28 py-6 border-t border-border"
      >
        <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3D] mb-4">
          Focused Resurfacing vs. Broader Pool Remodeling
        </h2>
        <div className="prose prose-lg max-w-none text-foreground leading-relaxed space-y-4">
          <p>
            Pool resurfacing primarily addresses the interior finish across the
            pool walls and floor. Pool remodeling can include resurfacing, but
            it extends beyond the interior when other components or design goals
            are involved.
          </p>
          <p>
            If the finish is worn or uncomfortable but the tile, coping, deck,
            layout, and features remain suitable, resurfacing may be enough.
          </p>
          <p>A broader remodel may make more sense when:</p>
          <ul>
            <li>The finish and waterline tile both need attention</li>
            <li>
              Coping is damaged, loose, dated, or incompatible with the new
              design
            </li>
            <li>The surrounding deck or pavers are also being replaced</li>
            <li>Several finishes no longer coordinate</li>
            <li>
              The owner wants to change how the pool area looks or functions
            </li>
            <li>
              Separate phases would create repeated demolition or avoidable
              disruption
            </li>
            <li>A larger outdoor-living project is already being planned</li>
          </ul>
          <p>
            Our guide to{" "}
            <Link
              to="/blog/pool-resurfacing-vs-pool-remodeling"
              className={linkCls}
            >
              pool resurfacing versus pool remodeling
            </Link>{" "}
            explains that central scope decision in more detail.
          </p>
          <p>
            One warning matters here: resurfacing should not be treated as a
            universal fix for water loss, recurring cracks, movement, plumbing
            concerns, or unexplained surface failure. Those symptoms may require
            further evaluation before the appropriate remodeling scope can be
            defined. Homeowners planning resurfacing can also use the{" "}
            <Link
              to="/blog/replace-pool-tile-coping-decking-during-resurfacing"
              className={linkCls}
            >
              tile, coping, and decking replacement guide
            </Link>{" "}
            to decide which connected components should be kept, repaired,
            replaced, or evaluated.
          </p>
        </div>
      </section>

      {/* Section 3 */}
      <section
        id="the-five-connected-parts-of-a-pool-remodel"
        className="scroll-mt-28 py-6 border-t border-border"
      >
        <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3D] mb-4">
          The Five Connected Parts of a Pool Remodel
        </h2>
        <div className="prose prose-lg max-w-none text-foreground leading-relaxed space-y-4">
          <p>
            A useful way to plan a remodel is to look at the pool as a group of
            connected layers rather than one isolated surface.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            1. Interior finish
          </h3>
          <p>
            The interior finish covers the pool walls and floor. It influences
            texture, appearance, perceived water color, comfort, and ongoing
            care.
          </p>
          <p>
            Finish categories may include plaster-based, quartz aggregate,
            pebble aggregate, or other appropriate systems. The right option
            depends on the pool, the desired appearance, the existing surface,
            maintenance expectations, and verified product availability. Compare
            plaster, quartz, pebble, and other options in the{" "}
            <Link
              to="/blog/pool-finish-comparison-south-florida"
              className={linkCls}
            >
              South Florida pool-finish comparison
            </Link>{" "}
            before naming the exact finish in the project scope.
          </p>
          <p>
            Finish selection should not happen in isolation. Waterline tile,
            coping, deck color, sunlight, shade, depth, and the surrounding
            environment can all affect how the completed pool appears.
          </p>
          <p>
            The existing surface also needs to be evaluated properly. Stains,
            roughness, discoloration, etching, hollow areas, or cracks do not
            all have the same cause or require the same preparation. If the
            primary concern is the interior surface, begin with the{" "}
            <Link to="/blog/signs-pool-needs-resurfacing" className={linkCls}>
              signs that a pool may need resurfacing
            </Link>{" "}
            and note which symptoms may require further evaluation.
          </p>
          <p>
            The{" "}
            <a
              href="https://www.npconline.org/page/swimming-pool-deterioration"
              target="_blank"
              rel="noopener noreferrer"
              className={extLinkCls}
            >
              National Plasterers Council
            </a>{" "}
            explains that cementitious finishes are affected by materials,
            application, startup procedures, ongoing care, and water chemistry.
            That is one reason surface condition should be evaluated before a
            new finish is selected.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            2. Waterline tile
          </h3>
          <p>
            Waterline tile creates the visual transition between the pool water
            and coping. It is also one of the most noticeable design elements
            around the pool perimeter.
          </p>
          <p>When choosing tile, consider:</p>
          <ul>
            <li>How its color works with the interior finish</li>
            <li>Whether the pattern fits the home and surrounding deck</li>
            <li>How the tile looks in direct sun and shade</li>
            <li>The scale of the tile relative to the pool</li>
            <li>How grout color changes the overall appearance</li>
            <li>Cleaning and maintenance expectations</li>
            <li>Whether the existing tile is sound enough to remain</li>
          </ul>
          <p>
            Precision&rsquo;s{" "}
            <Link to="/glass-tile-mosaics" className={linkCls}>
              glass tile and mosaic selection
            </Link>{" "}
            provides one route for homeowners researching visual options, but no
            photograph or screen can perfectly reproduce how a material will
            look beside an actual pool.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">3. Coping</h3>
          <p>
            Coping forms the edge around the pool and creates the transition
            between the pool structure and the surrounding deck.
          </p>
          <p>
            Its material, profile, dimensions, color, joints, and relationship
            to the deck all affect the finished result.
          </p>
          <p>Coping decisions should account for:</p>
          <ul>
            <li>Compatibility with the pool and deck</li>
            <li>The desired edge profile</li>
            <li>Surface texture</li>
            <li>Color and material variation</li>
            <li>How the coping meets steps, spas, raised walls, or features</li>
            <li>Whether deck replacement will affect the pool edge</li>
            <li>The condition and suitability of the existing coping</li>
          </ul>
          <p>
            Replacing coping is not automatically required during resurfacing.
            However, it should be evaluated before the interior and surrounding
            deck plans are finalized.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            4. Pool deck and pavers
          </h3>
          <p>
            The pool deck is not just the background around the water. It
            affects circulation, drainage, heat exposure, furniture placement,
            transitions, maintenance, and how the entire backyard feels.
          </p>
          <p>
            A{" "}
            <Link to="/services/pool-deck-remodeling" className={linkCls}>
              pool-deck remodeling project
            </Link>{" "}
            may involve replacing an existing surface, installing pavers,
            coordinating new coping, correcting transitions, or redesigning
            portions of the usable space.
          </p>
          <p>Important deck considerations include:</p>
          <ul>
            <li>Existing surface and base conditions</li>
            <li>Drainage and water movement</li>
            <li>
              Elevations near doors, patios, planters, and other structures
            </li>
            <li>Sun exposure and surface color</li>
            <li>Texture and wet-surface considerations</li>
            <li>Access for demolition and installation</li>
            <li>Furniture and walking paths</li>
            <li>
              Connections to patios, driveways, side yards, and landscaping
            </li>
          </ul>
          <p>
            Material should be chosen for the actual property&mdash;not from a
            showroom photo alone. A dark surface in full afternoon sun presents
            a different use case than a lighter material in a shaded yard.
          </p>
          <p>
            Precision&rsquo;s verified paver resources include{" "}
            <Link to="/brick-pavers" className={linkCls}>
              brick pavers
            </Link>
            ,{" "}
            <Link to="/travertine-pavers" className={linkCls}>
              travertine pavers
            </Link>
            , and{" "}
            <Link to="/marble-pavers" className={linkCls}>
              marble pavers
            </Link>
            . Availability and suitability should be confirmed for the
            individual project. The guide to the{" "}
            <Link
              to="/blog/best-pool-deck-materials-south-florida"
              className={linkCls}
            >
              best pool-deck materials for South Florida
            </Link>{" "}
            compares the practical tradeoffs among the primary material
            categories.
          </p>

          <SupportingVisual
            src={SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_COORDINATED}
            alt={SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_COORDINATED_ALT}
            caption={SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_COORDINATED_CAPTION}
            width={939}
            height={671}
          />

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            5. Features and the surrounding outdoor space
          </h3>
          <p>
            Some homeowners want to preserve the pool&rsquo;s existing layout
            and simply refresh its materials. Others want the pool area to work
            differently.
          </p>
          <p>That may mean coordinating the pool with:</p>
          <ul>
            <li>A surrounding patio</li>
            <li>Outdoor seating or entertaining areas</li>
            <li>Landscaping transitions</li>
            <li>Verified water or fire features</li>
            <li>A larger hardscape project</li>
            <li>Other verified outdoor enhancements</li>
          </ul>
          <p>
            The broader the goal, the more important it becomes to plan the pool
            and surrounding space as one environment.
          </p>
          <p>
            That does not mean every project needs to become a massive backyard
            rebuild. It means that connected work should be considered before
            one completed surface limits what can be done next.
          </p>
        </div>
      </section>
    </>
  );
}
